import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartDrawer() {
  const { items, isCartOpen, setCartOpen, setQty, removeItem, subtotal } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ product_id: i.id, size: i.size, quantity: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Please try again.");
      setCheckingOut(false);
    }
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 z-[65] h-full w-full max-w-md bg-[#18181C] border-l border-white/10 flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-white/10">
              <h2 className="font-display font-extrabold tracking-[0.2em] text-sm">YOUR BAG</h2>
              <button data-testid="cart-close-button" onClick={() => setCartOpen(false)} aria-label="Close cart" className="p-2 text-white/70 hover:text-[#D3FF24] transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <p data-testid="cart-empty-message" className="text-white/40 text-sm tracking-wide pt-16 text-center">
                  Your bag is empty. The iron is waiting.
                </p>
              ) : (
                <ul className="divide-y divide-white/10">
                  {items.map((i) => (
                    <li key={`${i.id}-${i.size}`} className="py-5 flex gap-4" data-testid={`cart-item-${i.id}`}>
                      <img src={i.image} alt={i.name} className="h-20 w-16 object-cover bg-[#0A0A0C]" />
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-2">
                          <h3 className="text-sm font-bold font-display tracking-wide truncate">{i.name}</h3>
                          <button
                            data-testid={`cart-remove-${i.id}`}
                            onClick={() => removeItem(i.id, i.size)}
                            aria-label={`Remove ${i.name}`}
                            className="text-white/40 hover:text-[#FF3B30] transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <p className="text-[10px] tracking-[0.2em] text-white/40 mt-1">SIZE {i.size}</p>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-white/15">
                            <button data-testid={`cart-qty-minus-${i.id}`} onClick={() => setQty(i.id, i.size, i.qty - 1)} className="p-1.5 text-white/60 hover:text-[#D3FF24]" aria-label="Decrease quantity">
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span className="w-8 text-center text-xs font-bold">{i.qty}</span>
                            <button data-testid={`cart-qty-plus-${i.id}`} onClick={() => setQty(i.id, i.size, Math.min(i.qty + 1, 10))} className="p-1.5 text-white/60 hover:text-[#D3FF24]" aria-label="Increase quantity">
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <span className="text-sm font-bold text-[#D3FF24]">${(i.price * i.qty).toFixed(2)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 px-6 py-6">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-white/50 tracking-widest text-xs">SUBTOTAL</span>
                  <span data-testid="cart-subtotal" className="font-display font-extrabold text-lg text-[#D3FF24]">
                    ${subtotal.toFixed(2)}
                  </span>
                </div>
                <p className="text-[10px] text-white/35 tracking-wide mb-5">Taxes calculated at checkout.</p>
                <button
                  data-testid="checkout-button"
                  onClick={checkout}
                  disabled={checkingOut}
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#D3FF24] text-[#0A0A0C] px-6 py-4 text-xs font-bold tracking-[0.25em] hover:bg-[#BEE61B] transition-colors duration-300 disabled:opacity-50"
                >
                  {checkingOut ? "REDIRECTING…" : "CHECKOUT"}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
